import { UpperCasePipe } from '@angular/common';
import { AfterContentInit, Component, DestroyRef, OnInit, ViewChild, ViewContainerRef, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AlertsFeedWidgetConfigFormComponent,
  ImageWidgetConfigFormComponent,
  LocationsWidgetConfigFormComponent,
  WidgetConfigFormTemplate,
  WidgetSelectionFormComponent
} from './widget-config-form-templates';
import { MapWallDisplayFacade, WidgetFormType } from '../../store';
import { TimesWidgetConfigFormComponent } from './widget-config-form-templates/times-widget-config-form/times-widget-config-form.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { defaultImageCardStyles } from '../../models/image-card-settings.model';

const formTemplateConfigMap = {
  [WidgetFormType.Select]: {
    headerLabel: 'MAP-DISPLAY.WALL-CONFIGURATION',
    component: WidgetSelectionFormComponent
  },
  [WidgetFormType.Times]: {
    headerLabel: 'MAP-DISPLAY.TIME-CONFIGURATION',
    component: TimesWidgetConfigFormComponent
  },
  [WidgetFormType.Locations]: {
    headerLabel: 'MAP-DISPLAY.LOCATION-CONFIGURATION',
    component: LocationsWidgetConfigFormComponent
  },
  [WidgetFormType.Image]: {
    headerLabel: 'MAP-DISPLAY.IMAGE-WIDGET.CONFIGURATION-LABEL',
    component: ImageWidgetConfigFormComponent
  },
  [WidgetFormType.AlertsFeed]: {
    headerLabel: 'MAP-DISPLAY.ALERTS-FEED-WIDGET.CONFIGURATION-LABEL',
    component: AlertsFeedWidgetConfigFormComponent
  }
};

@Component({
  selector: 'topo-wall-display-widget-config',
  imports: [TranslatePipe, UpperCasePipe],
  templateUrl: './wall-display-widget-config.component.html',
  styleUrl: './wall-display-widget-config.component.scss'
})
export class WallDisplayWidgetConfigComponent implements OnInit, AfterContentInit {
  private fb = inject(FormBuilder);
  protected state = inject(MapWallDisplayFacade);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private notify = inject(SystemNotificationsService);

  @ViewChild('widgetConfigTemplate', { static: true, read: ViewContainerRef }) templateContainer: ViewContainerRef;

  private form: FormGroup;
  protected headerLabel: string;
  protected showCloseButton = false;

  ngOnInit(): void {
    // * NOTE * - logic for containing all possible widget config form fields brought over from old component
    // * TBR * - redo widget config forms when decisions are made about the next iteration of Wall Display

    const {
      showLocations = false,
      showStatus = false,
      showTimes = false,
      showMapLegend = false,
      showAlertFeed = false,
      showImageCard = false,
      locationCardSettings: {
        showFlag = true,
        showTime = true,
        showTemperature = true,
        showWeather = true,
        showWorld = true,
        orderByLongitude: orderByLongitude_locationCard = false,
        transitionTimeSeconds = 15,
        timeFormat: timeFormat_locationCard = this.translate.instant('FORMAT.TIME-ONLY'),
        tempUnitsCelcius = false,
        worldTimeFormat = 'MMMM D, YYYY',
        worldShowDayNight = true
      } = {},
      timeCardSettings: {
        orderByLongitude: orderByLongitude_timeCard = false,
        timeFormat: timeFormat_timeCard = this.translate.instant('FORMAT.TIME-ONLY')
      } = {},
      imageCardSettings: {
        imageDataUrl = null,
        position = { top: 100, bottom: 0, left: 100, right: 0 }, // defaults to bottom right corner
        showBackground = true,
        cardStyles = defaultImageCardStyles
      } = {},
      alertsFeedSettings: { includedLayers = {}, respectLayerVisibility = false } = {}
    } = this.state.configuration() ?? {};

    this.form = this.fb.group({
      showLocations,
      showTimes,
      showStatus,
      showMapLegend,
      showAlertFeed,
      showImageCard,
      locationCardSettings: this.fb.group({
        showFlag,
        showTime,
        showTemperature,
        showWeather,
        showWorld,
        orderByLongitude: orderByLongitude_locationCard,
        transitionTimeSeconds,
        timeFormat: timeFormat_locationCard,
        tempUnitsCelcius,
        worldTimeFormat,
        worldShowDayNight
      }),
      timeCardSettings: this.fb.group({
        orderByLongitude: orderByLongitude_timeCard,
        timeFormat: timeFormat_timeCard
      }),
      imageCardSettings: this.fb.group({
        imageDataUrl,
        position,
        showBackground,
        cardStyles: this.fb.group({
          ...cardStyles
        })
      }),
      alertsFeedSettings: this.fb.group({
        includedLayers: this.fb.group(includedLayers),
        respectLayerVisibility
      })
    });
  }

  imageCardPositionEffect = effect(() => {
    const { position } = this.state.imageCardSettings() ?? {};
    const positionFormControl = this.form.get('imageCardSettings').get('position');
    const positionFormValue = positionFormControl.getRawValue();

    if (!!position) {
      const shouldUpdateForm = Object.entries(position).some(([direction, value]) => value !== positionFormValue[direction]);
      if (shouldUpdateForm) {
        positionFormControl.patchValue(position);
      }
    }
  });

  ngAfterContentInit(): void {
    this.state.widgetConfigFormType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(formType => {
      this.templateContainer.clear();

      const config = formTemplateConfigMap[formType];

      this.showCloseButton = formType !== WidgetFormType.Select;

      if (Boolean(config)) {
        const { component, headerLabel } = config;

        const componentRef = this.templateContainer.createComponent<WidgetConfigFormTemplate>(component);
        componentRef.instance.init({
          form: this.form
        });

        this.headerLabel = headerLabel;
      }
    });
  }

  applyConfigSettings(): void {
    if (this.form.invalid) {
      return;
    }

    const updates = this.form.getRawValue();
    const configuration = this.state.configuration();

    const updatedConfig = {
      ...configuration,
      ...updates
    };

    this.state.updateConfiguration(updatedConfig);

    const message = this.translate.instant('MAP-DISPLAY.CONFIGURATION-APPLIED');
    this.notify.info(message);
  }
}
